import React, { useEffect } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { track } from '@nexus/fff-analytics';

import { useUserContext } from '@nexus/fff-utilities';
import Layout from '../../components/Layout';
import MyBox from '../../components/MyBox';
import NotFoundError from '../NotFoundError';
import HeroBanner from '../../components/HeroBanner';
import useHeroBanner from './hooks/useHeroBanner';
import InvitePopup from '../../components/InvitePopup';
import InviteBanner from '../../components/InviteBanner';
import useWebCards from './hooks/useWebCards';
import useScreeMode from '../../hooks/useScreenMode';
import useMyBox from './hooks/useMyBox';
import useSeasonalTimelines from './hooks/useSeasonalTimelines';
import useInviteTotal from './hooks/useInviteTotal';
import useShowInvitePopup from './hooks/useShowInvitePopup';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import WebCards from '../../components/WebCards';
import SeasonalTimeline from '../../components/SeasonalTimeline';
import {
  Toast,
  showSuccessToast,
} from '../../shared-components/BaseToast/Toast';
import {
  isUnavailableStatus, isNotSubscribedStatus, isExpired, isHomepageLoading,
} from './helpers';
import { getLocalStorage, setLocalStorage } from '../../utils/localStorageHelpers';
import './Homepage.css';

const VIEWED = 'Experiment Viewed';

const Homepage = ({ flags, ldClient, test }) => {
  const { userInfo } = useUserContext();
  useLaunchDarkly(ldClient, userInfo);
  const screenMode = useScreeMode();
  const heroBanner = useHeroBanner();
  const myBox = useMyBox();
  const webCards = useWebCards();
  const seasonalTimelines = useSeasonalTimelines();
  const isSeasonalTimelinesVisible = flags.seasonalTimelineAbTest === true;
  const showReviewButton = myBox.data.eligible && !myBox.data.surveyStarted;
  const isLoading = isHomepageLoading({
    heroBanner, myBox, webCards, seasonalTimelines,
  });
  const inviteTotal = useInviteTotal(flags.homepageCallsNewInvitationApi);
  const { showInvitePopup, stateDispatch } = useShowInvitePopup('invite_popup_exp');
  const closeInvitePopup = () => {
    const invitePopupExpDate = new Date();
    invitePopupExpDate.setDate(invitePopupExpDate.getDate() + 7);
    setLocalStorage('invite_popup_exp', invitePopupExpDate);
    stateDispatch({ type: 'REQUEST_SHOW_INVITE_POPUP_FAIL' });
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    if (flags && Object.keys(flags).length > 0 && !test) {
      const variationName = isSeasonalTimelinesVisible ? 'Challenger' : 'Control';
      track(VIEWED, {
        experimentName: 'Homepage Rebuild Launch',
        variationName,
      });
    }
  }, [flags]);

  useEffect(() => {
    if (!flags.hideSgtfBannerAndPopup && showInvitePopup && inviteTotal.data > 0) {
      document.body.style.overflow = 'hidden';
    }
  }, [flags.hideSgtfBannerAndPopup, showInvitePopup, inviteTotal]);

  useEffect(() => {
    if (getLocalStorage('show_membership_reminder_banner')
      && getLocalStorage('show_membership_reminder_banner') !== null) {
      showSuccessToast(
        'We’ll remind you 5 days before your membership begins.',
      );
      setLocalStorage('show_membership_reminder_banner', null);
    }
  }, []);

  if (flags.newHomepage === false) {
    return <NotFoundError />;
  }

  if (flags.newHomepage === true) {
    return (
      <div className="homepage">
        {!flags.hideSgtfBannerAndPopup && inviteTotal.data > 0 && <InviteBanner inviteTotal={inviteTotal.data} />}
        <Layout isLoading={isLoading} redirectIfNoAuth>
          {!heroBanner.fail && Object.keys(heroBanner.data).length > 0 ? (
            <HeroBanner data={heroBanner.data} screenMode={screenMode} />
          ) : (
            <div className="hero_banner_placeholder" />
          )}
          {!flags.hideSgtfBannerAndPopup && showInvitePopup && inviteTotal.data > 0
          && <InvitePopup closeInvitePopup={closeInvitePopup} />}
          {isSeasonalTimelinesVisible && !seasonalTimelines.fail && Object.keys(seasonalTimelines.data).length > 0 && (
          <SeasonalTimeline
            isExpired={isExpired(myBox)}
            isNotSubscribed={isNotSubscribedStatus(myBox.data.status)}
            data={seasonalTimelines.data}
            screenMode={screenMode}
          />
          )}
          {Object.keys(myBox.data).length > 0 && (
          <MyBox
            data={myBox.data}
            screenMode={screenMode}
            isChallenge={isSeasonalTimelinesVisible}
            hasFailed={myBox.fail || isUnavailableStatus(myBox.data.status)}
            showReviewButton={showReviewButton}
          />
          )}
          {!webCards.fail && Object.keys(webCards.data).length > 0 ? (
            <WebCards data={webCards.data} screenMode={screenMode} />
          ) : (
            <div className="web-cards_placeholder" />
          )}
        </Layout>
        <Toast />
      </div>
    );
  }
  //This is on purpose since the feature flags is completed asynchronously and may be undefined.
  return null;
};

export default withLDConsumer()(Homepage);
